import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, ChevronDown, ChevronRight, Trash2, SquarePen } from 'lucide-react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { type CustomerFormData } from '../../types/customer';
import { customerService } from '../../services/customerService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { CustomerForm } from './CustomerForm';
import { SalesOrderMiniTable } from '../../components/selling/SalesOrderMiniTable';
import { useToast } from '../../components/common/Toast';
import { getErrorMessage } from '../../utils/error';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';

export const CustomerList: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { showToast } = useToast();
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('customers', 'write');
  const canDelete = can('customers', 'delete');

  const { page, size, setPage, setSize } = usePaginationState({ defaultSize: 25 });

  const { data, isLoading: loading } = useQuery({
    queryKey: ['customers', { page, size }],
    queryFn: () => customerService.list({ page, size }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const customers = data?.data ?? [];
  const total = data?.total ?? 0;

  const refreshCustomers = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const handleCreate = () => {
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('selling.customerList.confirmDelete'))) return;
    try {
      await customerService.delete(id);
      showToast(t('selling.customerList.toast.deleted'), 'success');
      refreshCustomers();
    } catch (err: unknown) {
      const rawMessage = getErrorMessage(err, t('selling.customerList.toast.deleteFailed'));
      const message =
        rawMessage.includes('violates foreign key constraint') || rawMessage.includes('SQLSTATE 23503')
          ? t('selling.customerList.toast.inUse')
          : rawMessage;
      showToast(message, 'error');
    }
  };

  const handleSave = async (data: CustomerFormData) => {
    try {
      if (editingId) {
        await customerService.update(editingId, data);
        showToast(t('selling.customerList.toast.updated'), 'success');
      } else {
        await customerService.create(data);
        showToast(t('selling.customerList.toast.created'), 'success');
      }
      setIsModalOpen(false);
      refreshCustomers();
    } catch (err: unknown) {
      showToast(getErrorMessage(err, t('selling.customerList.toast.saveFailed')), 'error');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('selling.customerList.title')}</h1>
        {canWrite && (
          <button
            onClick={handleCreate}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-test-id="customer-add-button"
          >
            {t('selling.customerList.actions.add')}
          </button>
        )}
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <LoadingSpinner text={t('selling.customerList.loading')} />
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('selling.customerList.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-6 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.email')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.phone')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.billingAddress')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.shippingAddress')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.messageOnInvoice')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.internalNotes')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('selling.customerList.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map(customer => (
                  <React.Fragment key={customer.id}>
                    <tr 
                      key={customer.id}
                      onClick={() => handleEdit(customer.id)} // Klik baris untuk edit
                      className={`hover:bg-gray-50 transition-colors ${canWrite ? 'cursor-pointer' : 'cursor-default'}`} 
                      data-test-id={`customer-row-${customer.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(customer.id);
                          }}
                          className="p-1 hover:bg-gray-200 rounded-full focus:outline-none"
                        >
                          {expandedId === customer.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{customer.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {customer.email ? (
                          <a 
                            href={`mailto:${customer.email}`} 
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary-600 hover:text-primary-900 flex items-center gap-2 hover:underline"
                          >
                            <Mail className="h-4 w-4" />
                            {customer.email}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {customer.phone ? (
                          <div className="flex items-center gap-2">
                            <span>{customer.phone}</span>
                            <div className="flex gap-1">
                              <a 
                                href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`} 
                                onClick={(e) => e.stopPropagation()}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
                                title={t('selling.customerList.actions.openWhatsApp')}
                              >
                                <MessageCircle className="h-4 w-4" />
                              </a>
                              <a 
                                href={`tel:${customer.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                                title={t('selling.customerList.actions.call')}
                              >
                                <Phone className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{customer.billingAddress || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{customer.shippingAddress || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{customer.messageOnInvoice || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{customer.internalNotes || '-'}</td>
                       <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-3">
                          {/* Tombol Edit */}
                          <button
                            data-test-id={`customer-edit-button-${customer.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(customer.id);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                            title={t('selling.customerList.actions.edit')}
                          >
                            <SquarePen className="w-4 h-4" />
                            {canWrite ? t('selling.customerList.actions.edit') : t('common.view')}
                          </button>

                          {/* Tombol Delete */}
                          {canDelete && (
                            <button
                              data-test-id={`customer-delete-button-${customer.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(customer.id);
                              }}
                              className="p-2 items-center text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 flex flex-row gap-2 font-bold"
                              title={t('selling.customerList.actions.delete')}
                            >
                              <Trash2 size={18} />
                              {t('selling.customerList.actions.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === customer.id && (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                          <SalesOrderMiniTable customerId={customer.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} pageSize={size} total={total} onPageChange={setPage} onPageSizeChange={setSize} isLoading={loading} />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t('selling.customerList.modal.editTitle') : t('selling.customerList.modal.addTitle')}
        maxWidth="2xl"
      >
        <CustomerForm
          initialData={customers.find(c => c.id === editingId)}
          onSubmit={handleSave}
          onCancel={() => setIsModalOpen(false)}
          existingCodes={customers.filter(c => c.id !== editingId).map(c => c.code || '')}
          readOnly={!canWrite}
        />
      </Modal>
    </div>
  );
};
